import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getOrder, getOrders, startOrder, completeOrder, createOrder } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useOffline } from '../context/OfflineContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { StatusBadge } from '../components/StatusBadge';
import { formatOrderNo, formatDate, formatCurrency } from '../lib/utils';
import { toast } from 'sonner';
import { ArrowLeft, MapPin, Calendar, Play, CheckCircle2, Plus, Trash2, Loader2, ExternalLink, Navigation, Clock, Pause, Timer, Keyboard, WifiOff, Car, CheckSquare, Square, Lock, Copy } from 'lucide-react';

// Helper function to format date to local datetime-local input format
const toLocalDateTimeString = (date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
};

export const EmployeeOrderDetailPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { isOnline, getCachedOrder, queueForSync } = useOffline();
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [starting, setStarting] = useState(false);
    const [completing, setCompleting] = useState(false);
    const [showCompleteDialog, setShowCompleteDialog] = useState(false);
    const [isOfflineData, setIsOfflineData] = useState(false);
    
    // Stopwatch state - persisted in localStorage
    const [isRunning, setIsRunning] = useState(false);
    const [elapsedTime, setElapsedTime] = useState(0); // in seconds
    const [stopwatchStartTime, setStopwatchStartTime] = useState(null);
    const intervalRef = useRef(null);
    
    // Load stopwatch state from localStorage on mount
    useEffect(() => {
        const savedStopwatch = localStorage.getItem(`stopwatch_${id}`);
        if (savedStopwatch) {
            try {
                const { startTime, wasRunning } = JSON.parse(savedStopwatch);
                if (startTime && wasRunning) {
                    const start = new Date(startTime);
                    const now = new Date();
                    const elapsed = Math.floor((now.getTime() - start.getTime()) / 1000);
                    setStopwatchStartTime(start);
                    setElapsedTime(Math.max(0, elapsed));
                    setIsRunning(true);
                }
            } catch (e) {
                // Silent fail - stopwatch state optional
            }
        }
    }, [id]);
    
    // Save stopwatch state to localStorage when it changes
    useEffect(() => {
        if (stopwatchStartTime && isRunning) {
            localStorage.setItem(`stopwatch_${id}`, JSON.stringify({
                startTime: stopwatchStartTime.toISOString(),
                wasRunning: true
            }));
        }
    }, [id, stopwatchStartTime, isRunning]);
    
    // Clear localStorage when stopwatch is reset or order completed
    const clearStopwatchStorage = () => {
        localStorage.removeItem(`stopwatch_${id}`);
    };
    
    // Time input mode: 'stopwatch' or 'manual'
    const [timeInputMode, setTimeInputMode] = useState('stopwatch');
    
    // Completion form state
    const [completionForm, setCompletionForm] = useState({
        started_at: '',
        finished_at: '',
        labor_hours: 0,
        travel_hours: 0,
        materials: '',
        notes: '',
        extra_work_notes: '',
        customer_signature_name: '',
        actual_lines: [],
        completed_tasks: [], // Track which planned tasks were completed
        work_items: [] // Track work items completion status
    });
    
    // Additional work and materials state
    const [additionalWork, setAdditionalWork] = useState([]);
    const [usedMaterials, setUsedMaterials] = useState([]);
    
    // Follow-up order dialog state
    const [showFollowUpDialog, setShowFollowUpDialog] = useState(false);
    const [incompleteWorkItems, setIncompleteWorkItems] = useState([]);
    const [creatingFollowUp, setCreatingFollowUp] = useState(false);
    
    // Image upload state
    const [uploadedImages, setUploadedImages] = useState([]);
    const [uploadingImage, setUploadingImage] = useState(false);
    const imageInputRef = useRef(null);

    useEffect(() => {
        loadOrder();
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    // Stopwatch effect
    useEffect(() => {
        if (isRunning) {
            intervalRef.current = setInterval(() => {
                setElapsedTime(prev => prev + 1);
            }, 1000);
        } else {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        }
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [isRunning]);

    const [canStartOrder, setCanStartOrder] = useState(false);
    const [isOrderLocked, setIsOrderLocked] = useState(false);

    const loadOrder = async () => {
        try {
            const res = await getOrder(id);
            setOrder(res.data);
            setIsOfflineData(false);
            
            // Pre-fill completion form with planned tasks
            const now = new Date();
            const plannedTaskIds = res.data.planned_lines?.map(line => line.id) || [];
            
            // Initialize work items - Muss vom Benutzer aktiv als erledigt markiert werden
            const workItems = (res.data.work_items || []).map(item => ({
                id: item.id,
                description: item.description,
                completed: false,
                not_completed_reason: ''
            }));
            
            // AUTO-RESUME STOPWATCH: If order is IN_PROGRESS, find the start time from history
            if (res.data.status === 'IN_PROGRESS') {
                const history = res.data.history || [];
                // Find when the order was started (look for status change to IN_PROGRESS)
                const startEntry = history.find(h => 
                    h.new_status === 'IN_PROGRESS' || 
                    h.action === 'Auftrag gestartet' ||
                    h.action === 'Arbeit gestartet'
                );
                
                if (startEntry && startEntry.timestamp) {
                    const startTime = new Date(startEntry.timestamp);
                    const elapsedSeconds = Math.floor((now.getTime() - startTime.getTime()) / 1000);
                    
                    // Set the stopwatch to the elapsed time
                    setStopwatchStartTime(startTime);
                    setElapsedTime(Math.max(0, elapsedSeconds));
                    setIsRunning(true); // Auto-start the stopwatch
                    
                    // Pre-fill started_at with actual start time
                    setCompletionForm(prev => ({
                        ...prev,
                        started_at: toLocalDateTimeString(startTime),
                        finished_at: toLocalDateTimeString(now),
                        completed_tasks: plannedTaskIds,
                        work_items: workItems
                    }));
                } else {
                    // Fallback if no history entry found
                    setCompletionForm(prev => ({
                        ...prev,
                        started_at: toLocalDateTimeString(now),
                        finished_at: toLocalDateTimeString(now),
                        completed_tasks: plannedTaskIds,
                        work_items: workItems
                    }));
                }
            } else {
                setCompletionForm(prev => ({
                    ...prev,
                    started_at: toLocalDateTimeString(now),
                    finished_at: toLocalDateTimeString(now),
                    completed_tasks: plannedTaskIds,
                    work_items: workItems
                }));
            }
            
            // Check if this order can be started (sequence enforcement)
            await checkCanStart(res.data);
        } catch (err) {
            // Try to load from cache if offline
            if (!isOnline) {
                const cachedOrder = await getCachedOrder(id);
                if (cachedOrder) {
                    setOrder(cachedOrder);
                    setIsOfflineData(true);
                    setCanStartOrder(true); // Allow in offline mode
                    toast.info('Offline-Modus: Daten aus dem Cache geladen');
                    setLoading(false);
                    return;
                }
            }
            toast.error('Auftrag nicht gefunden');
            navigate('/my-orders');
        } finally {
            setLoading(false);
        }
    };

    const checkCanStart = async (currentOrder) => {
        // If already in progress, can always continue
        if (currentOrder.status === 'IN_PROGRESS') {
            setCanStartOrder(true);
            setIsOrderLocked(false);
            return;
        }
        
        // If status is DONE or other, no action needed
        if (currentOrder.status !== 'ASSIGNED') {
            setCanStartOrder(false);
            setIsOrderLocked(false);
            return;
        }
        
        try {
            const res = await getOrders();
            const myOrders = res.data
                .filter(o => 
                    o.assigned_to_user_id === user?.id && 
                    ['ASSIGNED', 'IN_PROGRESS'].includes(o.status)
                )
                .sort((a, b) => a.order_no - b.order_no);
            
            // Check if any order is in progress
            const hasInProgress = myOrders.some(o => o.status === 'IN_PROGRESS');
            if (hasInProgress) {
                setCanStartOrder(false);
                setIsOrderLocked(true);
                // Redirect back with message
                toast.error('Sie haben bereits einen Auftrag in Bearbeitung. Bitte schließen Sie diesen zuerst ab.');
                navigate('/my-orders');
                return;
            }
            
            // Check if this is the first assigned order
            const firstAssigned = myOrders.find(o => o.status === 'ASSIGNED');
            const canStart = firstAssigned?.id === currentOrder.id;
            setCanStartOrder(canStart);
            setIsOrderLocked(!canStart);
            
            if (!canStart) {
                // Redirect back with message
                toast.error('Dieser Auftrag ist noch gesperrt. Bitte arbeiten Sie die vorherigen Aufträge zuerst ab.');
                navigate('/my-orders');
            }
        } catch (err) {
            setCanStartOrder(true); // Fallback to allow
            setIsOrderLocked(false);
        }
    };

    const handleStart = async () => {
        if (!canStartOrder) {
            toast.error('Sie müssen zuerst den vorherigen Auftrag abschließen');
            return;
        }
        setStarting(true);
        try {
            await startOrder(id);
            toast.success('Auftrag gestartet');
            // Start the stopwatch automatically
            setStopwatchStartTime(new Date());
            setIsRunning(true);
            loadOrder();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Fehler beim Starten');
        } finally {
            setStarting(false);
        }
    };

    // Stopwatch controls
    const startStopwatch = () => {
        if (!stopwatchStartTime) {
            setStopwatchStartTime(new Date());
        }
        setIsRunning(true);
    };

    const pauseStopwatch = () => {
        setIsRunning(false);
    };

    const resetStopwatch = () => {
        setIsRunning(false);
        setElapsedTime(0);
        setStopwatchStartTime(null);
        clearStopwatchStorage(); // Clear localStorage
    };

    const formatStopwatchTime = (seconds) => {
        const hrs = Math.floor(seconds / 3600);
        const mins = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;
        return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };
    
    // Additional work functions
    const addAdditionalWork = () => {
        setAdditionalWork([...additionalWork, { description: '', notes: '' }]);
    };
    
    const updateAdditionalWork = (index, field, value) => {
        const updated = [...additionalWork];
        updated[index][field] = value;
        setAdditionalWork(updated);
    };
    
    const removeAdditionalWork = (index) => {
        setAdditionalWork(additionalWork.filter((_, i) => i !== index));
    };
    
    // Used materials functions
    const addUsedMaterial = () => {
        setUsedMaterials([...usedMaterials, { name: '', quantity: '', notes: '' }]);
    };
    
    const updateUsedMaterial = (index, field, value) => {
        const updated = [...usedMaterials];
        updated[index][field] = value;
        setUsedMaterials(updated);
    };
    
    const removeUsedMaterial = (index) => {
        setUsedMaterials(usedMaterials.filter((_, i) => i !== index));
    };

    const openCompleteDialog = () => {
        const now = new Date();
        
        // Calculate labor hours from stopwatch if used
        const laborHoursFromStopwatch = elapsedTime / 3600;
        
        // Prüfen ob bereits work_items im State vorhanden sind (Zwischenspeicherung)
        const existingWorkItems = completionForm.work_items || [];
        const orderWorkItemIds = (order.work_items || []).map(item => item.id);
        
        // Verwende existierende work_items wenn sie zum aktuellen Auftrag passen
        const hasMatchingWorkItems = existingWorkItems.length > 0 && 
            existingWorkItems.every(item => orderWorkItemIds.includes(item.id));
        
        let workItemsToUse;
        if (hasMatchingWorkItems) {
            // Behalte den vorherigen Stand, aber füge neue Items hinzu falls vorhanden
            const existingIds = existingWorkItems.map(item => item.id);
            const newItems = (order.work_items || [])
                .filter(item => !existingIds.includes(item.id))
                .map(item => ({
                    id: item.id,
                    description: item.description,
                    completed: false,
                    not_completed_reason: ''
                }));
            workItemsToUse = [...existingWorkItems, ...newItems];
        } else {
            // Keine passenden work_items vorhanden - initialisiere neu
            workItemsToUse = (order.work_items || []).map(item => ({
                id: item.id,
                description: item.description,
                completed: false,  // Muss vom Benutzer aktiv als erledigt markiert werden
                not_completed_reason: ''
            }));
        }
        
        // Determine the correct start time:
        // 1. Use stopwatch start time if available (most accurate)
        // 2. Use existing form value if already set
        // 3. Fallback to current time (should rarely happen)
        let startedAtValue;
        if (stopwatchStartTime) {
            startedAtValue = toLocalDateTimeString(stopwatchStartTime);
        } else if (completionForm.started_at && completionForm.started_at.length > 0) {
            startedAtValue = completionForm.started_at;
        } else {
            // Fallback: use current time minus elapsed time if stopwatch was running
            if (elapsedTime > 0) {
                const estimatedStart = new Date(now.getTime() - (elapsedTime * 1000));
                startedAtValue = toLocalDateTimeString(estimatedStart);
            } else {
                startedAtValue = toLocalDateTimeString(now);
            }
        }
        
        setCompletionForm(prev => ({
            ...prev,
            finished_at: toLocalDateTimeString(now),
            started_at: startedAtValue,
            labor_hours: timeInputMode === 'stopwatch' ? Math.round(laborHoursFromStopwatch * 100) / 100 : prev.labor_hours,
            work_items: workItemsToUse
        }));
        setShowCompleteDialog(true);
    };

    const addMaterialLine = () => {
        setCompletionForm(prev => ({
            ...prev,
            actual_lines: [
                ...prev.actual_lines,
                { type: 'MATERIAL', name: '', qty: 1, unit: 'pcs', unit_price_net: 0, tax_rate: 19 }
            ]
        }));
    };

    const updateLine = (index, field, value) => {
        const newLines = [...completionForm.actual_lines];
        newLines[index] = { ...newLines[index], [field]: value };
        setCompletionForm(prev => ({ ...prev, actual_lines: newLines }));
    };

    const removeLine = (index) => {
        setCompletionForm(prev => ({
            ...prev,
            actual_lines: prev.actual_lines.filter((_, i) => i !== index)
        }));
    };

    const toggleTaskCompletion = (taskId) => {
        setCompletionForm(prev => {
            const isCompleted = prev.completed_tasks.includes(taskId);
            return {
                ...prev,
                completed_tasks: isCompleted 
                    ? prev.completed_tasks.filter(id => id !== taskId)
                    : [...prev.completed_tasks, taskId]
            };
        });
    };

    // Auto-calculate labor hours from start and end times
    const calculateLaborHours = (startStr, endStr) => {
        if (!startStr || !endStr) return 0;
        
        const start = new Date(startStr);
        const end = new Date(endStr);
        
        if (isNaN(start.getTime()) || isNaN(end.getTime())) return 0;
        
        let diffMs = end - start;
        if (diffMs < 0) diffMs += 24 * 60 * 60 * 1000; // Handle overnight
        
        const hours = diffMs / (1000 * 60 * 60);
        return Math.round(hours * 10) / 10;
    };

    // Update labor hours when times change
    const handleTimeChange = (field, value) => {
        setCompletionForm(prev => {
            const updated = { ...prev, [field]: value };
            // Auto-calculate hours
            const hours = calculateLaborHours(updated.started_at, updated.finished_at);
            return { ...updated, labor_hours: hours };
        });
    };

    // Image upload handler with compression
    const handleImageUpload = async (event) => {
        const files = Array.from(event.target.files || []);
        if (files.length === 0) return;
        
        setUploadingImage(true);
        
        for (const file of files) {
            try {
                // Compress image before uploading
                const compressedBase64 = await compressImage(file, 800, 0.7);
                setUploadedImages(prev => [...prev, {
                    id: Date.now() + Math.random(),
                    name: file.name,
                    data: compressedBase64,
                    type: 'image/jpeg'
                }]);
            } catch (err) {
                toast.error(`Fehler beim Laden von ${file.name}`);
            }
        }
        
        setUploadingImage(false);
        if (imageInputRef.current) {
            imageInputRef.current.value = '';
        }
    };
    
    // Compress image to reduce size
    const compressImage = (file, maxWidth, quality) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                const img = new Image();
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    let width = img.width;
                    let height = img.height;
                    
                    // Resize if too large
                    if (width > maxWidth) {
                        height = (height * maxWidth) / width;
                        width = maxWidth;
                    }
                    
                    canvas.width = width;
                    canvas.height = height;
                    
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0, width, height);
                    
                    // Convert to compressed JPEG
                    const compressedBase64 = canvas.toDataURL('image/jpeg', quality);
                    resolve(compressedBase64);
                };
                img.onerror = reject;
                img.src = e.target.result;
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    };
    
    const removeImage = (imageId) => {
        setUploadedImages(prev => prev.filter(img => img.id !== imageId));
    };

    const handleComplete = async (e) => {
        e.preventDefault();
        
        // Only validate labor hours for HOURLY billing type
        const isHourly = order.billing_type !== 'FIXED';
        
        if (isHourly && completionForm.labor_hours < 0) {
            toast.error('Arbeitsstunden müssen >= 0 sein');
            return;
        }

        // Check work items - all must be completed OR have a reason
        // Admins can skip this check completely
        const isAdmin = user?.role === 'ADMIN';
        
        if (!isAdmin && completionForm.work_items?.length > 0) {
            const incompleteWithoutReason = completionForm.work_items.filter(
                item => item.completed === false && (!item.not_completed_reason || item.not_completed_reason.trim() === '')
            );
            
            if (incompleteWithoutReason.length > 0) {
                toast.error(`Bitte Grund für nicht erledigte Position "${incompleteWithoutReason[0].description}" angeben`);
                return;
            }
        }

        // Check if all planned tasks are completed
        const allTasksCompleted = order.planned_lines?.every(line => 
            completionForm.completed_tasks.includes(line.id)
        ) ?? true;
        
        if (!allTasksCompleted) {
            const confirmPartial = window.confirm('Nicht alle Aufgaben sind abgehakt. Trotzdem abschließen?');
            if (!confirmPartial) return;
        }
        
        // Combine notes with extra work notes and incomplete tasks
        let combinedNotes = completionForm.notes || '';
        if (completionForm.extra_work_notes) {
            combinedNotes += (combinedNotes ? '\n\n' : '') + '--- MEHRARBEIT ---\n' + completionForm.extra_work_notes;
        }
        
        // Add incomplete tasks to notes
        const incompleteTasks = order.planned_lines?.filter(line => 
            !completionForm.completed_tasks.includes(line.id)
        ) || [];
        if (incompleteTasks.length > 0) {
            combinedNotes += (combinedNotes ? '\n\n' : '') + '--- NICHT ERLEDIGT ---\n' + 
                incompleteTasks.map(t => `- ${t.name}`).join('\n');
        }
        
        // Only include time data for HOURLY billing type
        const submitData = {
            ...completionForm,
            notes: combinedNotes,
            // For FIXED billing, set times to null/0
            started_at: isHourly ? completionForm.started_at : null,
            finished_at: isHourly ? completionForm.finished_at : null,
            labor_hours: isHourly ? completionForm.labor_hours : 0,
            travel_hours: isHourly ? completionForm.travel_hours : 0,
            // Backend expects 'work_item_completions', not 'work_items'
            work_item_completions: completionForm.work_items?.map(item => ({
                id: item.id,
                completed: item.completed,
                not_completed_reason: item.completed ? null : item.not_completed_reason
            })) || [],
            // Additional work and materials
            additional_work: additionalWork.filter(w => w.description.trim()),
            used_materials: usedMaterials.filter(m => m.name.trim()),
            // Convert images to backend format: {filename, data}
            images: uploadedImages.map(img => ({
                filename: img.name,
                data: img.data
            }))
        };
        
        // Remove work_items from submitData since we use work_item_completions
        delete submitData.work_items;
        
        setCompleting(true);
        try {
            await completeOrder(id, submitData);
            toast.success('Auftrag abgeschlossen und E-Mail versendet');
            setShowCompleteDialog(false);
            clearStopwatchStorage(); // Clear stopwatch from localStorage
            
            // Check if there are incomplete work items and ask about follow-up order
            const incomplete = (completionForm.work_items || []).filter(item => !item.completed);
            if (incomplete.length > 0) {
                setIncompleteWorkItems(incomplete);
                setShowFollowUpDialog(true);
            } else {
                navigate('/my-orders');
            }
        } catch (err) {
            // If offline, queue for later sync
            if (!isOnline) {
                const API_URL = process.env.REACT_APP_BACKEND_URL;
                const queued = await queueForSync(`${API_URL}/api/orders/${id}/complete`, 'POST', submitData);
                if (queued) {
                    toast.success('Offline gespeichert - wird synchronisiert, sobald Sie wieder online sind');
                    setShowCompleteDialog(false);
                    clearStopwatchStorage(); // Clear stopwatch from localStorage
                    navigate('/my-orders');
                    return;
                }
            }
            toast.error(err.response?.data?.detail || 'Fehler beim Abschließen');
        } finally {
            setCompleting(false);
        }
    };
    
    const handleCreateFollowUpOrder = async () => {
        setCreatingFollowUp(true);
        try {
            const followUpData = {
                customer_id: order.customer_id,
                title: `Folgeauftrag: ${order.title}`,
                description: `Folgeauftrag von ${formatOrderNo(order.order_no)}\n\nNicht erledigte Positionen:\n${incompleteWorkItems.map(item => `- ${item.description}: ${item.not_completed_reason}`).join('\n')}`,
                service_address: order.service_address,
                work_items: incompleteWorkItems.map(item => ({
                    description: item.description
                })),
                billing_type: order.billing_type || 'EFFORT',
                priority: order.priority || 'MEDIUM'
            };
            
            const response = await createOrder(followUpData);
            toast.success('Folgeauftrag erstellt!');
            setShowFollowUpDialog(false);
            setIncompleteWorkItems([]);
            
            // Navigate to the orders list (employee can't access admin order detail)
            navigate('/my-orders');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Fehler beim Erstellen des Folgeauftrags');
        } finally {
            setCreatingFollowUp(false);
        }
    };

    const openMap = () => {
        const address = order.service_address || order.customer?.billing_address;
        if (address) {
            window.open(`https://maps.google.com/?q=${encodeURIComponent(address)}`, '_blank');
        }
    };

    const startNavigation = () => {
        const address = order.service_address || order.customer?.billing_address;
        if (address) {
            window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}&travelmode=driving`, '_blank');
        } else {
            toast.error('Keine Adresse verfügbar');
        }
    };

    if (loading) {
        return (
            <div className="p-6 flex items-center justify-center min-h-screen">
                <div className="spinner" />
            </div>
        );
    }

    if (!order) return null;

    return (
        <div className="p-4 md:p-6 space-y-4 pb-24" data-testid="employee-order-detail-page">
            {/* Offline Banner */}
            {isOfflineData && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center gap-3">
                    <WifiOff className="h-5 w-5 text-amber-600" />
                    <div>
                        <p className="font-medium text-amber-800">Offline-Modus</p>
                        <p className="text-sm text-amber-600">Diese Daten stammen aus dem Cache</p>
                    </div>
                </div>
            )}
            
            {/* Header */}
            <div className="flex items-start gap-3">
                <Button variant="ghost" size="icon" onClick={() => navigate('/my-orders')} data-testid="back-btn">
                    <ArrowLeft className="h-5 w-5" />
                </Button>
                <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                        <span className="order-no text-primary">{formatOrderNo(order.order_no)}</span>
                        <StatusBadge status={order.status} />
                    </div>
                    <h1 className="text-xl font-bold">{order.title}</h1>
                    <p className="text-sm text-muted-foreground">{order.customer?.name}</p>
                </div>
            </div>

            {/* Address Card */}
            <Card className="card-hover">
                <CardContent className="p-4">
                    <div className="flex items-center gap-3 mb-3" onClick={openMap}>
                        <div className="h-10 w-10 rounded-sm bg-primary/10 flex items-center justify-center">
                            <MapPin className="h-5 w-5 text-primary" />
                        </div>
                        <div className="flex-1">
                            <p className="text-sm text-muted-foreground">Einsatzort</p>
                            <p className="font-medium">{order.service_address || order.customer?.billing_address}</p>
                        </div>
                        <ExternalLink className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <Button 
                        className="w-full bg-primary hover:bg-primary/90 h-12 text-base"
                        onClick={startNavigation}
                        data-testid="navigation-btn"
                    >
                        <Navigation className="h-5 w-5 mr-2" />
                        Navigation starten
                    </Button>
                </CardContent>
            </Card>

            {/* Date */}
            {order.planned_date && (
                <Card>
                    <CardContent className="p-4 flex items-center gap-3">
                        <div className="h-10 w-10 rounded-sm bg-primary/10 flex items-center justify-center">
                            <Calendar className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                            <p className="text-sm text-muted-foreground">Geplant</p>
                            <p className="font-medium">{formatDate(order.planned_date)}</p>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Stopwatch - Only show when order is IN_PROGRESS */}
            {order.status === 'IN_PROGRESS' && (
                <Card className="border-2 border-primary/20">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center gap-2">
                            <Timer className="h-5 w-5" />
                            Zeiterfassung
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="text-center mb-4">
                            <div className="text-4xl font-mono font-bold text-primary mb-2">
                                {formatStopwatchTime(elapsedTime)}
                            </div>
                            <p className="text-sm text-muted-foreground">
                                {isRunning ? 'Läuft seit Auftragsstart...' : (elapsedTime > 0 ? 'Pausiert' : 'Bereit')}
                            </p>
                            {stopwatchStartTime && (
                                <p className="text-xs text-muted-foreground mt-1">
                                    Gestartet: {stopwatchStartTime.toLocaleString('de-DE')}
                                </p>
                            )}
                        </div>
                        <div className="flex gap-2">
                            {!isRunning ? (
                                <Button 
                                    className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                                    onClick={startStopwatch}
                                    data-testid="start-stopwatch-btn"
                                >
                                    <Play className="h-4 w-4 mr-2" />
                                    {elapsedTime > 0 ? 'Fortsetzen' : 'Starten'}
                                </Button>
                            ) : (
                                <Button 
                                    className="flex-1"
                                    variant="outline"
                                    onClick={pauseStopwatch}
                                    data-testid="pause-stopwatch-btn"
                                >
                                    <Pause className="h-4 w-4 mr-2" />
                                    Pause
                                </Button>
                            )}
                            {elapsedTime > 0 && (
                                <Button 
                                    variant="outline"
                                    onClick={resetStopwatch}
                                    data-testid="reset-stopwatch-btn"
                                >
                                    Zurücksetzen
                                </Button>
                            )}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Description */}
            {order.description && (
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base">Beschreibung</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p className="text-sm whitespace-pre-wrap">{order.description}</p>
                    </CardContent>
                </Card>
            )}

            {/* Planned Positions */}
            {order.planned_lines?.length > 0 && (
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base">Geplante Leistungen</CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                        <Table>
                            <TableBody>
                                {order.planned_lines.map((line, i) => (
                                    <TableRow key={line.id || i}>
                                        <TableCell>
                                            <p className="font-medium">{line.name}</p>
                                            <p className="text-xs text-muted-foreground">{line.type}</p>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            {line.qty} {line.unit}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            )}

            {/* Action Buttons */}
            <div className="fixed bottom-20 left-0 right-0 p-4 bg-background border-t md:relative md:bottom-0 md:border-0 md:p-0 md:bg-transparent">
                {order.status === 'ASSIGNED' && (
                    <>
                        {canStartOrder ? (
                            <Button 
                                className="w-full h-12 text-lg btn-scale" 
                                onClick={handleStart}
                                disabled={starting}
                                data-testid="start-btn"
                            >
                                {starting ? (
                                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                                ) : (
                                    <Play className="h-5 w-5 mr-2" />
                                )}
                                Auftrag starten
                            </Button>
                        ) : (
                            <div className="bg-muted rounded-sm p-4 text-center">
                                <Lock className="h-6 w-6 mx-auto mb-2 text-muted-foreground" />
                                <p className="text-sm font-medium text-muted-foreground">
                                    Dieser Auftrag ist noch gesperrt
                                </p>
                                <p className="text-xs text-muted-foreground mt-1">
                                    Bitte schließen Sie zuerst den vorherigen Auftrag ab
                                </p>
                            </div>
                        )}
                    </>
                )}
                {order.status === 'IN_PROGRESS' && (
                    <Button 
                        className="w-full h-12 text-lg btn-scale bg-emerald-600 hover:bg-emerald-700" 
                        onClick={openCompleteDialog}
                        data-testid="complete-btn"
                    >
                        <CheckCircle2 className="h-5 w-5 mr-2" />
                        Fertigstellen
                    </Button>
                )}
            </div>

            {/* Completion Dialog */}
            <Dialog open={showCompleteDialog} onOpenChange={setShowCompleteDialog}>
                <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="complete-dialog">
                    <DialogHeader>
                        <DialogTitle>Auftrag abschließen</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleComplete} className="space-y-6">
                        
                        {/* Work Items Section - MUST BE FIRST */}
                        {completionForm.work_items?.length > 0 && (
                            <div className="space-y-3">
                                <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wider">
                                    Arbeitspositionen bestätigen
                                </h4>
                                {completionForm.work_items.map((item, index) => (
                                    <div 
                                        key={item.id || index}
                                        className={`p-3 border rounded-lg ${item.completed ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}
                                    >
                                        <div className="flex items-center gap-3 mb-2">
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    const newItems = [...completionForm.work_items];
                                                    newItems[index].completed = !newItems[index].completed;
                                                    if (newItems[index].completed) {
                                                        newItems[index].not_completed_reason = '';
                                                    }
                                                    setCompletionForm({...completionForm, work_items: newItems});
                                                }}
                                                className={`p-2 rounded-md ${item.completed ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`}
                                            >
                                                {item.completed ? <CheckSquare className="h-5 w-5" /> : <Square className="h-5 w-5" />}
                                            </button>
                                            <span className="font-medium flex-1">{item.description}</span>
                                        </div>
                                        {!item.completed && (
                                            <Input
                                                placeholder="Grund warum nicht erledigt (Pflicht) *"
                                                value={item.not_completed_reason}
                                                onChange={(e) => {
                                                    const newItems = [...completionForm.work_items];
                                                    newItems[index].not_completed_reason = e.target.value;
                                                    setCompletionForm({...completionForm, work_items: newItems});
                                                }}
                                                className="mt-2"
                                            />
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                        {/* Time Input Mode Selection */}
                        <div className="space-y-4">
                            <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wider">Zeiterfassung</h4>
                            
                            {/* Time Input - Only for HOURLY billing type */}
                            {order.billing_type !== 'FIXED' ? (
                                <Tabs value={timeInputMode} onValueChange={setTimeInputMode} className="w-full">
                                    <TabsList className="grid w-full grid-cols-2">
                                        <TabsTrigger value="stopwatch" className="flex items-center gap-2" data-testid="tab-stopwatch">
                                            <Timer className="h-4 w-4" />
                                            Stoppuhr
                                        </TabsTrigger>
                                        <TabsTrigger value="manual" className="flex items-center gap-2" data-testid="tab-manual">
                                            <Keyboard className="h-4 w-4" />
                                            Manuell
                                        </TabsTrigger>
                                    </TabsList>
                                    
                                    <TabsContent value="stopwatch" className="mt-4">
                                        <div className="bg-muted/50 p-4 rounded-sm text-center">
                                            <p className="text-sm text-muted-foreground mb-2">Gestoppte Zeit</p>
                                            <p className="text-3xl font-mono font-bold text-primary">
                                                {formatStopwatchTime(elapsedTime)}
                                            </p>
                                            <p className="text-sm text-muted-foreground mt-2">
                                                = {(elapsedTime / 3600).toFixed(2)} Stunden
                                            </p>
                                        </div>
                                    </TabsContent>
                                    
                                    <TabsContent value="manual" className="mt-4 space-y-4">
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <Label>Beginn</Label>
                                                <Input
                                                    type="datetime-local"
                                                    value={completionForm.started_at}
                                                    onChange={(e) => handleTimeChange('started_at', e.target.value)}
                                                    required
                                                    data-testid="started-at-input"
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label>Ende</Label>
                                                <Input
                                                    type="datetime-local"
                                                    value={completionForm.finished_at}
                                                    onChange={(e) => handleTimeChange('finished_at', e.target.value)}
                                                    required
                                                    data-testid="finished-at-input"
                                                />
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Arbeitsstunden (auto-berechnet)</Label>
                                            <Input
                                                type="number"
                                                step="0.1"
                                                min="0"
                                                value={completionForm.labor_hours}
                                                onChange={(e) => setCompletionForm({...completionForm, labor_hours: parseFloat(e.target.value) || 0})}
                                                required
                                                data-testid="labor-hours-input"
                                                className="bg-blue-50 border-blue-200"
                                            />
                                        </div>
                                    </TabsContent>
                                </Tabs>
                            ) : (
                                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                                    <p className="text-sm text-blue-700">
                                        <strong>Pauschalauftrag</strong> - Keine Zeiterfassung erforderlich
                                    </p>
                                </div>
                            )}
                            
                            {/* Travel Time - More prominent - Only for HOURLY */}
                            {order.billing_type !== 'FIXED' && (
                            <div className="p-4 border-2 border-primary/30 bg-primary/5 rounded-sm space-y-2">
                                <Label className="flex items-center gap-2 text-primary font-medium">
                                    <Car className="h-4 w-4" />
                                    Fahrtzeit (Stunden)
                                </Label>
                                <Input
                                    type="number"
                                    step="0.25"
                                    min="0"
                                    value={completionForm.travel_hours}
                                    onChange={(e) => setCompletionForm({...completionForm, travel_hours: parseFloat(e.target.value) || 0})}
                                    className="bg-white"
                                    data-testid="travel-hours-input"
                                />
                                <p className="text-xs text-muted-foreground">
                                    Hin- und Rückfahrt zusammen
                                </p>
                            </div>
                            )}
                        </div>

                        {/* Planned Tasks Checklist - NEW SECTION */}
                        {order.planned_lines?.length > 0 && (
                            <div className="space-y-3">
                                <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wider">
                                    Aufgaben vom Admin ({completionForm.completed_tasks.length}/{order.planned_lines.length} erledigt)
                                </h4>
                                <div className="border rounded-sm divide-y">
                                    {order.planned_lines.map((line) => {
                                        const isCompleted = completionForm.completed_tasks.includes(line.id);
                                        return (
                                            <div 
                                                key={line.id}
                                                className={`p-3 flex items-center gap-3 cursor-pointer transition-colors ${isCompleted ? 'bg-primary/5' : 'bg-white hover:bg-muted/50'}`}
                                                onClick={() => toggleTaskCompletion(line.id)}
                                                data-testid={`task-${line.id}`}
                                            >
                                                {isCompleted ? (
                                                    <CheckSquare className="h-5 w-5 text-primary shrink-0" />
                                                ) : (
                                                    <Square className="h-5 w-5 text-muted-foreground shrink-0" />
                                                )}
                                                <div className="flex-1">
                                                    <p className={`font-medium ${isCompleted ? 'line-through text-muted-foreground' : ''}`}>
                                                        {line.name}
                                                    </p>
                                                    <p className="text-xs text-muted-foreground">
                                                        {line.qty} {line.unit} • {line.type}
                                                    </p>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Extra Work Notes - NEW SECTION */}
                        <div className="space-y-2 p-4 border-2 border-amber-200 bg-amber-50 rounded-sm">
                            <Label className="flex items-center gap-2 text-amber-800">
                                <Clock className="h-4 w-4" />
                                Mehrarbeit / Zusätzliche Leistungen
                            </Label>
                            <Textarea
                                value={completionForm.extra_work_notes}
                                onChange={(e) => setCompletionForm({...completionForm, extra_work_notes: e.target.value})}
                                placeholder="Falls mehr Arbeit geleistet wurde als geplant, bitte hier vermerken..."
                                rows={3}
                                className="bg-white"
                                data-testid="extra-work-notes-input"
                            />
                            <p className="text-xs text-amber-700">
                                Hier können Sie dokumentieren, wenn zusätzliche Arbeiten durchgeführt wurden.
                            </p>
                        </div>

                        {/* Materials Section */}
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wider">Materialien</h4>
                                <Button type="button" variant="outline" size="sm" onClick={addMaterialLine} data-testid="add-material-btn">
                                    <Plus className="h-4 w-4 mr-1" />
                                    Hinzufügen
                                </Button>
                            </div>
                            {completionForm.actual_lines.length > 0 && (
                                <div className="space-y-3">
                                    {completionForm.actual_lines.map((line, index) => (
                                        <div key={line.id || `material-line-${index}`} className="p-3 border rounded-sm space-y-3" data-testid={`material-line-${index}`}>
                                            <div className="flex justify-between items-center">
                                                <span className="text-sm text-muted-foreground">Material {index + 1}</span>
                                                <Button type="button" variant="ghost" size="icon" onClick={() => removeLine(index)}>
                                                    <Trash2 className="h-4 w-4 text-destructive" />
                                                </Button>
                                            </div>
                                            <Input
                                                placeholder="Bezeichnung"
                                                value={line.name}
                                                onChange={(e) => updateLine(index, 'name', e.target.value)}
                                            />
                                            <div className="grid grid-cols-3 gap-2">
                                                <Input
                                                    type="number"
                                                    placeholder="Menge"
                                                    value={line.qty}
                                                    onChange={(e) => updateLine(index, 'qty', parseFloat(e.target.value) || 0)}
                                                />
                                                <Select value={line.unit} onValueChange={(v) => updateLine(index, 'unit', v)}>
                                                    <SelectTrigger>
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="pcs">Stück</SelectItem>
                                                        <SelectItem value="m">Meter</SelectItem>
                                                        <SelectItem value="kg">kg</SelectItem>
                                                        <SelectItem value="l">Liter</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                                <Input
                                                    type="number"
                                                    placeholder="€ Netto"
                                                    value={line.unit_price_net}
                                                    onChange={(e) => updateLine(index, 'unit_price_net', parseFloat(e.target.value) || 0)}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                            <div className="space-y-2">
                                <Label>Materialnotizen (Freitext)</Label>
                                <Textarea
                                    value={completionForm.materials}
                                    onChange={(e) => setCompletionForm({...completionForm, materials: e.target.value})}
                                    placeholder="Weitere verwendete Materialien..."
                                    rows={2}
                                    data-testid="materials-input"
                                />
                            </div>
                        </div>

                        {/* General Notes Section */}
                        <div className="space-y-2">
                            <Label>Allgemeine Notizen</Label>
                            <Textarea
                                value={completionForm.notes}
                                onChange={(e) => setCompletionForm({...completionForm, notes: e.target.value})}
                                placeholder="Anmerkungen zum Auftrag..."
                                rows={3}
                                data-testid="notes-input"
                            />
                        </div>
                        
                        {/* Additional Work Section */}
                        <div className="space-y-3 border-t pt-4">
                            <div className="flex items-center justify-between">
                                <Label className="text-base font-medium">Zusätzliche Arbeiten</Label>
                                <Button 
                                    type="button" 
                                    variant="outline" 
                                    size="sm"
                                    onClick={addAdditionalWork}
                                    data-testid="add-additional-work-btn"
                                >
                                    <Plus className="h-4 w-4 mr-1" />
                                    Hinzufügen
                                </Button>
                            </div>
                            {additionalWork.length === 0 ? (
                                <p className="text-sm text-muted-foreground">Keine zusätzlichen Arbeiten erfasst</p>
                            ) : (
                                <div className="space-y-3">
                                    {additionalWork.map((work, index) => (
                                        <div key={`add-work-${index}`} className="flex gap-2 items-start p-3 bg-muted/50 rounded-lg">
                                            <div className="flex-1 space-y-2">
                                                <Input
                                                    placeholder="Beschreibung der Arbeit *"
                                                    value={work.description}
                                                    onChange={(e) => updateAdditionalWork(index, 'description', e.target.value)}
                                                    data-testid={`additional-work-${index}-desc`}
                                                />
                                                <Input
                                                    placeholder="Anmerkung (optional)"
                                                    value={work.notes || ''}
                                                    onChange={(e) => updateAdditionalWork(index, 'notes', e.target.value)}
                                                    data-testid={`additional-work-${index}-notes`}
                                                />
                                            </div>
                                            <Button 
                                                type="button"
                                                variant="ghost" 
                                                size="icon"
                                                onClick={() => removeAdditionalWork(index)}
                                                className="text-destructive"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        
                        {/* Used Materials Section */}
                        <div className="space-y-3 border-t pt-4">
                            <div className="flex items-center justify-between">
                                <Label className="text-base font-medium">Verwendete Materialien</Label>
                                <Button 
                                    type="button" 
                                    variant="outline" 
                                    size="sm"
                                    onClick={addUsedMaterial}
                                    data-testid="add-material-btn"
                                >
                                    <Plus className="h-4 w-4 mr-1" />
                                    Hinzufügen
                                </Button>
                            </div>
                            {usedMaterials.length === 0 ? (
                                <p className="text-sm text-muted-foreground">Keine Materialien erfasst</p>
                            ) : (
                                <div className="space-y-3">
                                    {usedMaterials.map((material, index) => (
                                        <div key={`material-${index}`} className="flex gap-2 items-start p-3 bg-muted/50 rounded-lg">
                                            <div className="flex-1 space-y-2">
                                                <div className="flex gap-2">
                                                    <Input
                                                        placeholder="Material/Produkt *"
                                                        value={material.name}
                                                        onChange={(e) => updateUsedMaterial(index, 'name', e.target.value)}
                                                        className="flex-1"
                                                        data-testid={`material-${index}-name`}
                                                    />
                                                    <Input
                                                        placeholder="Menge (z.B. 5 Stück)"
                                                        value={material.quantity || ''}
                                                        onChange={(e) => updateUsedMaterial(index, 'quantity', e.target.value)}
                                                        className="w-32"
                                                        data-testid={`material-${index}-qty`}
                                                    />
                                                </div>
                                                <Input
                                                    placeholder="Anmerkung (optional)"
                                                    value={material.notes || ''}
                                                    onChange={(e) => updateUsedMaterial(index, 'notes', e.target.value)}
                                                    data-testid={`material-${index}-notes`}
                                                />
                                            </div>
                                            <Button 
                                                type="button"
                                                variant="ghost" 
                                                size="icon"
                                                onClick={() => removeUsedMaterial(index)}
                                                className="text-destructive"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Customer Signature */}
                        <div className="space-y-2">
                            <Label>Kundenunterschrift (Name)</Label>
                            <Input
                                value={completionForm.customer_signature_name}
                                onChange={(e) => setCompletionForm({...completionForm, customer_signature_name: e.target.value})}
                                placeholder="Name des Kunden"
                                data-testid="signature-name-input"
                            />
                        </div>

                        {/* Image Upload */}
                        <div className="space-y-2">
                            <Label>Fotos hinzufügen</Label>
                            <input
                                ref={imageInputRef}
                                type="file"
                                accept="image/*"
                                multiple
                                onChange={handleImageUpload}
                                className="hidden"
                                id="image-upload"
                            />
                            <Button 
                                type="button" 
                                variant="outline" 
                                className="w-full"
                                onClick={() => imageInputRef.current?.click()}
                                disabled={uploadingImage}
                            >
                                {uploadingImage ? (
                                    <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Lädt...</>
                                ) : (
                                    <><Plus className="h-4 w-4 mr-2" /> Fotos auswählen</>
                                )}
                            </Button>
                            
                            {/* Uploaded Images Preview */}
                            {uploadedImages.length > 0 && (
                                <div className="grid grid-cols-3 gap-2 mt-2">
                                    {uploadedImages.map((img) => (
                                        <div key={img.id} className="relative">
                                            <img 
                                                src={img.data} 
                                                alt={img.name}
                                                className="w-full h-20 object-cover rounded border"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => removeImage(img.id)}
                                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                                            >
                                                ×
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Submit */}
                        <div className="flex gap-3 pt-4">
                            <Button type="button" variant="outline" className="flex-1" onClick={() => setShowCompleteDialog(false)}>
                                Abbrechen
                            </Button>
                            <Button type="submit" className="flex-1" disabled={completing} data-testid="submit-complete-btn">
                                {completing && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                                Speichern & Senden
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>
            
            {/* Follow-up Order Dialog */}
            <Dialog open={showFollowUpDialog} onOpenChange={setShowFollowUpDialog}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Copy className="h-5 w-5 text-orange-500" />
                            Folgeauftrag erstellen?
                        </DialogTitle>
                        <DialogDescription>
                            Es gibt nicht erledigte Arbeitspositionen. Möchten Sie einen Folgeauftrag erstellen?
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-3">
                        <Label className="text-sm font-medium">Nicht erledigte Positionen:</Label>
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                            {incompleteWorkItems.map((item, index) => (
                                <div key={item.id || `incomplete-${index}`} className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                                    <p className="font-medium text-sm">{item.description}</p>
                                    <p className="text-xs text-muted-foreground mt-1">
                                        Grund: {item.not_completed_reason}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                    
                    <DialogFooter className="flex gap-2 sm:gap-0">
                        <Button 
                            variant="outline" 
                            onClick={() => {
                                setShowFollowUpDialog(false);
                                setIncompleteWorkItems([]);
                                navigate('/my-orders');
                            }}
                        >
                            Nein, danke
                        </Button>
                        <Button 
                            onClick={handleCreateFollowUpOrder} 
                            disabled={creatingFollowUp}
                            className="bg-orange-500 hover:bg-orange-600"
                        >
                            {creatingFollowUp && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                            <Copy className="h-4 w-4 mr-2" />
                            Folgeauftrag erstellen
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default EmployeeOrderDetailPage;
